import React, { useState, useEffect } from 'react';
import { ChevronRight, ChevronDown, Search } from 'lucide-react';
import { 
  UtensilsCrossed, 
  Sparkles, 
  Heart, 
  Wrench, 
  ShoppingBag, 
  HardHat, 
  GraduationCap, 
  Tractor,
  Palmtree,
  Car,
  Laptop,
  Banknote,
  Scale,
  Building,
  Truck,
  PartyPopper,
  Home as HomeIcon,
  Dog,
  Factory,
  Radio,
  Building2,
  Dumbbell,
  Paintbrush,
  Palette
} from 'lucide-react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { api } from '../services/api';
import Breadcrumbs from '../components/Breadcrumbs';
import SEOHead from '../components/SEOHead';


// Icon mapping - mesmo padrão do CategoryCard
const iconMap = {
  UtensilsCrossed,
  Sparkles,
  Heart,
  Wrench,
  ShoppingBag,
  HardHat,
  GraduationCap,
  Tractor,
  Palmtree,
  Car,
  Laptop,
  Banknote,
  Scale,
  Building,
  Truck,
  PartyPopper,
  Home: HomeIcon,
  Dog,
  Factory,
  Radio,
  Building2,
  Dumbbell,
  Paintbrush,
  Palette
};

export default function Categorias() {
  const navigate = useNavigate();
  const { municipioSlug } = useParams();
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [municipio, setMunicipio] = useState(null);

  useEffect(() => {
    loadCategories();
  }, []);

  // Load municipality info
  useEffect(() => {
    const loadMunicipio = async () => {
      if (municipioSlug) {
        try {
          const res = await api.get('/municipios');
          const found = res.data.find(m => m.slug === municipioSlug);
          if (found) setMunicipio(found);
        } catch (e) {
          console.error('Error loading municipio:', e);
        }
      }
    };
    loadMunicipio();
  }, [municipioSlug]);

  useEffect(() => {
    // Expand category from URL parameter
    const categoryParam = searchParams.get('categoria');
    if (categoryParam) {
      setExpandedCategory(categoryParam);
    }
  }, [searchParams]);

  const loadCategories = async () => {
    try {
      const res = await api.get('/categories');
      setCategories(res.data);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (categoryId) => {
    if (expandedCategory === categoryId) {
      setExpandedCategory(null);
    } else {
      setExpandedCategory(categoryId);
    }
  };

  const handleSubcategoryClick = (categoryId, subcategoria) => {
    navigate(`/explorar?categoria=${categoryId}&subcategoria=${encodeURIComponent(subcategoria)}`);
  };

  const handleViewAllCategory = (categoryId) => {
    navigate(`/explorar?categoria=${categoryId}`);
  };

  // Filter categories based on search
  const filteredCategories = categories.filter(cat => {
    const query = searchQuery.toLowerCase();
    if (cat.nome.toLowerCase().includes(query)) return true;
    if (cat.subcategorias?.some(sub => sub.toLowerCase().includes(query))) return true;
    return false;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--background)] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-3 border-[var(--primary)] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--background)] pb-20">
      <SEOHead
        title={municipio ? `Categorias de negócios em ${municipio.nome}, ${municipio.uf || 'MG'}` : 'Categorias de Negócios'}
        description={municipio
          ? `Veja todas as categorias e subcategorias de negócios em ${municipio.nome}. Encontre o que precisa no Empreende Comunidade.`
          : 'Veja todas as categorias de negócios disponíveis. Navegue por serviços, comércio e profissionais locais.'
        }
        canonical={municipioSlug ? `/${municipioSlug}/categorias` : '/categorias'}
      />
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[var(--primary)] to-[#7B2FA8] text-white py-8">
        <div className="max-w-7xl mx-auto px-4">
          <Breadcrumbs 
            items={[
              { label: 'Categorias', path: null }
            ]}
            municipio={municipio}
            className="mb-4"
          />
          <h1 className="text-3xl md:text-4xl font-bold mb-3" style={{ fontFamily: 'Manrope' }}>
            Categorias
          </h1>
          <p className="text-white/90 text-lg">
            Encontre negócios por categoria e subcategoria
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Search */}
        <div className="mb-8 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar categorias ou subcategorias..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-[var(--border)] bg-white focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)] transition-all"
            data-testid="category-search-input"
          />
        </div>

        {/* Categories List */}
        <div className="space-y-4">
          {filteredCategories.map(category => {
            const isExpanded = expandedCategory === category.id;
            const Icon = iconMap[category.icone] || UtensilsCrossed;
            
            return (
              <div 
                key={category.id}
                className="bg-white rounded-2xl border border-[var(--border)] overflow-hidden shadow-sm"
                data-testid={`category-card-${category.id}`}
              >
                {/* Category Header */}
                <button
                  onClick={() => handleCategoryClick(category.id)}
                  className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#F3E8FF] rounded-xl flex items-center justify-center">
                      <Icon className="w-6 h-6 text-[var(--primary)]" />
                    </div>
                    <div className="text-left">
                      <h2 className="font-bold text-lg">{category.nome}</h2>
                      <p className="text-sm text-[var(--text-secondary)]">
                        {category.subcategorias?.length || 0} subcategorias
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewAllCategory(category.id);
                      }}
                      className="text-sm text-[var(--primary)] font-medium hover:underline px-3 py-1"
                    >
                      Ver todos
                    </button>
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </button>

                {/* Subcategories */}
                {isExpanded && category.subcategorias && category.subcategorias.length > 0 && (
                  <div className="border-t border-[var(--border)] bg-gray-50 p-4">
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                      {category.subcategorias.map((sub, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleSubcategoryClick(category.id, sub)}
                          className="text-left p-3 rounded-xl bg-white border border-[var(--border)] hover:border-[var(--primary)] hover:bg-[var(--primary)]/5 transition-all text-sm font-medium"
                          data-testid={`subcategory-${category.id}-${idx}`}
                        >
                          {sub}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* No subcategories message */}
                {isExpanded && (!category.subcategorias || category.subcategorias.length === 0) && (
                  <div className="border-t border-[var(--border)] bg-gray-50 p-4 text-center">
                    <p className="text-sm text-[var(--text-secondary)]">
                      Nenhuma subcategoria disponível
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {filteredCategories.length === 0 && (
          <div className="text-center py-12">
            <p className="text-[var(--text-secondary)]">
              Nenhuma categoria encontrada para "{searchQuery}"
            </p>
          </div>
        )}
      </div>

    </div>
  );
}
